import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, of, share, switchMap, withLatestFrom } from 'rxjs';
import { SupportUsersActions } from '@states/support-users/support-users.actions';
import { supportUsersFeature } from '@states/support-users/support-users.feature';
import { UtilsService } from '../../edge/utils.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { SupportUsersService } from '../../services/support-users.service';

const toSharedError = (utils: UtilsService, response: HttpErrorResponse | Error) =>
  SharedActions.showMessage({ error: utils.errMessage(response) });

export const startLoadingSupportUser$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SupportUsersActions.startLoadingSupportUsers),
      exhaustMap(() => [
        SupportUsersActions.setIsLoading({ isLoading: true }),
        SupportUsersActions.loadSupportUsers(),
      ]),
    ),
  { functional: true },
);

export const loadSupportUsers$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    supportUsersService = inject(SupportUsersService),
    utils = inject(UtilsService),
  ) =>
    actions$.pipe(
      ofType(SupportUsersActions.loadSupportUsers),
      withLatestFrom(store.select(supportUsersFeature.selectSupportUsersState)),
      switchMap(([, { page, perPage, filters, isLastPage }]) => {
        if (isLastPage) {
          return of(SupportUsersActions.setIsLoading({ isLoading: false }));
        }

        return supportUsersService.getUsers(page, perPage, filters).pipe(
          switchMap((res) => {
            return [
              SupportUsersActions.loadSupportUsersSuccess({ users: res }),
              SupportUsersActions.setIsLoading({ isLoading: false }),
            ]
          }),
          catchError((response) => [
            SupportUsersActions.loadSupportUsersFail(),
            SupportUsersActions.setIsLoading({ isLoading: false }),
            toSharedError(utils, response),
          ]),
        );
      }),
      share(),
    ),
  { functional: true },
);

export const filterTrigger$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(SupportUsersActions.setFilters),
      switchMap(() => [
        SupportUsersActions.setIsLoading({ isLoading: true }),
        SupportUsersActions.resetList(),
        SupportUsersActions.loadSupportUsers(),
      ]),
    ),
  { functional: true },
);